import { useEffect, useState } from "react";
import { Grid2 as Grid, Paper, Stack } from "@mui/material";
import CardStage from "./components/OrderCard";
import OrderStageHeader from "@/pages/organization-orders/components/OrderStageHeader";
import { setContainer } from "@/store/uiSlice";
import { useAppDispatch } from "@/hooks";
import { Order as IOrder, Order, OrderStatus, PaymentMethodsType } from "@/types";
import { isEmpty } from "lodash";
import useOrganizations from "@/features/organizations/useOrganizations";
import DeliveryDisabledMessage from "./DeliveryDisabledMessage";
import { Fire03Icon, HandPrayerIcon, Search01Icon } from "@hugeicons/core-free-icons";
import { useIncomingOrders } from "./IncomingOrdersProvider";

type StageOrders = Record<string, IOrder[]>;

const STAGES = {
  [OrderStatus.REVIEWING]: {
    title: "Revisando",
    icon: Search01Icon,
  },
  [OrderStatus.PREPARING]: {
    title: "Preparando",
    icon: Fire03Icon,
  },
  [OrderStatus.SENDING]: {
    title: "Entregando",
    icon: HandPrayerIcon,
  },
};

interface Props {
  activeOrders: Order[];
}

const OrdersStages = ({ activeOrders }: Props) => {
  const dispatch = useAppDispatch();
  const [localOrders, setLocalOrders] = useState<StageOrders>({});
  const { currentOrganization } = useOrganizations();
  const { orders: reviewingOrders } = useIncomingOrders();

  const isDeliveryEnabled = currentOrganization?.settings?.services?.delivery?.isEnabled !== false;
  const stripeEnabled = Boolean(currentOrganization?.settings?.paymentMethods?.stripeEnabled);

  useEffect(() => {
    const isWaitingStripePayment = (o: Order) => {
      const isCard = o.paymentDetails?.method === PaymentMethodsType.CREDIT_CARD;
      const isPaid = o.paymentDetails?.status === "paid" || Boolean(o.paidAt);
      return o.status === OrderStatus.PENDING_PAYMENT || (stripeEnabled && isCard && !isPaid && o.status === OrderStatus.PREPARING);
    };

    const ordersByStatus: Record<string, Order[]> = {
      [OrderStatus.REVIEWING]: [
        ...reviewingOrders,
        ...activeOrders.filter((o) => isWaitingStripePayment(o)),
      ],
      [OrderStatus.PREPARING]: activeOrders.filter((o) => o.status === OrderStatus.PREPARING && !isWaitingStripePayment(o)),
      [OrderStatus.SENDING]: activeOrders.filter((o) => o.status === OrderStatus.SENDING || o.status === OrderStatus.WAITING),
    };

    setLocalOrders(ordersByStatus);
  }, [reviewingOrders, activeOrders, stripeEnabled]);

  useEffect(() => {
    dispatch(setContainer("fluid"));
    return () => {
      dispatch(setContainer("fixed"));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Grid container spacing={1.5} sx={{ height: "100%" }}>
      {Object.entries(STAGES).map(([stageId, stage]) => {
        const sortedOrders =
          localOrders[stageId]?.sort((a, b) => {
            return a.createdAt - b.createdAt;
          }) || [];

        return (
          <Grid
            key={stageId}
            size={{
              xs: 12,
              lg: 4,
            }}
          >
            <Paper sx={{ height: "100%" }}>
              <OrderStageHeader title={stage.title} icon={stage.icon} quantity={0} />
              <Stack
                spacing={2}
                style={{
                  padding: 12,
                  background: "transparent",
                  height: "100%"
                }}
              >
                {!isDeliveryEnabled && stageId === OrderStatus.REVIEWING && sortedOrders.length === 0 ? (
                  <DeliveryDisabledMessage organization={currentOrganization} />
                ) : !isEmpty(localOrders)
                  ? sortedOrders.map((order) => (
                    <div key={order.id}>
                      <CardStage {...order} />
                    </div>
                  ))
                  : null}
              </Stack>
            </Paper>
          </Grid>
        );
      })}
    </Grid>
  );
};

export default OrdersStages;
